import * as React from 'react';
import { Box, HStack, Text, Button } from '@chakra-ui/react';
import { RenderFormElements } from '@components/form';
import { EmptyStateBox } from './empty-state';
import type { ButtonProps } from '@chakra-ui/react';
import { InfoTip, Tooltip, toaster } from '@ui';
import { AiOutlineExport } from 'react-icons/ai';
import { IoIosEye } from 'react-icons/io';

type DetailItem = { label: string; value: React.ReactNode };

// Theme configuration for PDF viewer
export interface PdfViewerTheme {
  body: {
    margin: string;
    fontFamily: string;
    background: string;
    color: string;
  };
  container: {
    display: string;
    flexDirection: string;
    gap: string;
    padding: string;
  };
  header: {
    display: string;
    alignItems: string;
    gap: string;
  };
  title: {
    fontSize: string;
    fontWeight: string;
  };
  details: {
    display: string;
    gridTemplateColumns: string;
    gap: string;
  };
  item: {
    display: string;
    flexDirection: string;
    gap: string;
    padding: string;
    border: string;
    borderRadius: string;
    background: string;
  };
  label: {
    fontSize: string;
    color: string;
  };
  value: {
    fontSize: string;
    color: string;
    wordBreak: string;
  };
  frameWrap: {
    height: string;
    width: string;
    border: string;
    borderRadius: string;
    overflow: string;
    background: string;
  };
  iframe: {
    width: string;
    height: string;
    border: string;
  };
}

// Default dark theme
export const darkPdfViewerTheme: PdfViewerTheme = {
  body: {
    margin: '0',
    fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif',
    background: '#0b0b0b',
    color: '#eaeaea',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '16px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
  },
  details: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '8px 16px',
  },
  item: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '8px',
    border: '1px solid #2a2a2a',
    borderRadius: '8px',
    background: '#121212',
  },
  label: {
    fontSize: '12px',
    color: '#a0a0a0',
  },
  value: {
    fontSize: '14px',
    color: '#eaeaea',
    wordBreak: 'break-all',
  },
  frameWrap: {
    height: 'calc(100vh - 200px)',
    width: '100%',
    border: '1px solid #2a2a2a',
    borderRadius: '8px',
    overflow: 'hidden',
    background: '#121212',
  },
  iframe: {
    width: '100%',
    height: '100%',
    border: '0',
  },
};

// Light theme option
export const lightPdfViewerTheme: PdfViewerTheme = {
  body: {
    margin: '0',
    fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif',
    background: '#ffffff',
    color: '#1a1a1a',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '16px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
  },
  details: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '8px 16px',
  },
  item: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '8px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    background: '#f7fafc',
  },
  label: {
    fontSize: '12px',
    color: '#718096',
  },
  value: {
    fontSize: '14px',
    color: '#1a1a1a',
    wordBreak: 'break-all',
  },
  frameWrap: {
    height: 'calc(100vh - 200px)',
    width: '100%',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    overflow: 'hidden',
    background: '#ffffff',
  },
  iframe: {
    width: '100%',
    height: '100%',
    border: '0',
  },
};

// Helper function to generate CSS from theme
const generateStylesFromTheme = (theme: PdfViewerTheme): string => {
  return `
    body { 
      margin: ${theme.body.margin}; 
      font-family: ${theme.body.fontFamily}; 
      background: ${theme.body.background}; 
      color: ${theme.body.color}; 
    }
    .container { 
      display: ${theme.container.display}; 
      flex-direction: ${theme.container.flexDirection}; 
      gap: ${theme.container.gap}; 
      padding: ${theme.container.padding}; 
    }
    .header { 
      display: ${theme.header.display}; 
      align-items: ${theme.header.alignItems}; 
      gap: ${theme.header.gap}; 
    }
    .title { 
      font-size: ${theme.title.fontSize}; 
      font-weight: ${theme.title.fontWeight}; 
    }
    .details { 
      display: ${theme.details.display}; 
      grid-template-columns: ${theme.details.gridTemplateColumns}; 
      gap: ${theme.details.gap}; 
    }
    .item { 
      display: ${theme.item.display}; 
      flex-direction: ${theme.item.flexDirection}; 
      gap: ${theme.item.gap}; 
      padding: ${theme.item.padding}; 
      border: ${theme.item.border}; 
      border-radius: ${theme.item.borderRadius}; 
      background: ${theme.item.background}; 
    }
    .label { 
      font-size: ${theme.label.fontSize}; 
      color: ${theme.label.color}; 
    }
    .value { 
      font-size: ${theme.value.fontSize}; 
      color: ${theme.value.color}; 
      word-break: ${theme.value.wordBreak}; 
    }
    .frameWrap { 
      height: ${theme.frameWrap.height}; 
      width: ${theme.frameWrap.width}; 
      border: ${theme.frameWrap.border}; 
      border-radius: ${theme.frameWrap.borderRadius}; 
      overflow: ${theme.frameWrap.overflow}; 
      background: ${theme.frameWrap.background}; 
    }
    iframe { 
      width: ${theme.iframe.width}; 
      height: ${theme.iframe.height}; 
      border: ${theme.iframe.border}; 
    }
  `;
};

// Configuration interface for PDF opening
export interface PdfOpenConfig {
  theme?: 'dark' | 'light';
  messages?: {
    noPdfsError?: string;
    successTitle?: string;
    successDescription?: (count: number, warningCount: number) => string;
    warningTitle?: string;
    warningDescription?: (partNumber: string) => string;
  };
  defaultPageTitle?: string;
}

export interface OpenPdfTabButtonProps extends Omit<ButtonProps, 'onClick'> {
  url: string;
  pdfTitle: string;
  pageTitle?: string;
  details?: DetailItem[];
  buttonText?: string | React.ReactNode | null;
  buttonIcon?: React.ReactNode;
  buttonProps?: ButtonProps;
  isDisabled?: boolean;
  isInfoTip?: boolean;
  theme?: 'dark' | 'light';
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onBeforeOpen?: (e: React.MouseEvent<HTMLButtonElement>) => Promise<
    | {
        url?: string | null;
        pdfTitle?: string;
        pageTitle?: string;
        details?: DetailItem[];
      }
    | string
    | void
  >;
  [key: string]: any;
}

export const OpenPdfTabButton: React.FC<OpenPdfTabButtonProps> = ({
  url,
  pdfTitle,
  pageTitle = 'Document Preview',
  details = [],
  buttonText = 'Open Preview',
  buttonIcon = <IoIosEye />,
  isDisabled = false,
  isInfoTip = true,
  theme = 'dark',
  buttonProps,
  onClick,
  onBeforeOpen,
  ...rest
}) => {
  const [isLoading, setIsLoading] = React.useState(false);

  const handleOpen = React.useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      try {
        setIsLoading(true);

        let effectiveUrl = url;
        let effectivePdfTitle = pdfTitle;
        let effectivePageTitle = pageTitle;
        let effectiveDetails = details;

        if (onBeforeOpen) {
          const result = await onBeforeOpen(e);
          if (typeof result === 'string') {
            effectiveUrl = result || effectiveUrl;
          } else if (result && typeof result === 'object') {
            effectiveUrl = result.url ?? effectiveUrl;
            effectivePdfTitle = result.pdfTitle ?? effectivePdfTitle;
            effectivePageTitle = result.pageTitle ?? effectivePageTitle;
            effectiveDetails = result.details ?? effectiveDetails;
          }
        }

        if (!effectiveUrl) return;

        if (onClick) {
          onClick(e);
          return;
        }

        const styles = generateStylesFromTheme(
          theme === 'dark' ? darkPdfViewerTheme : lightPdfViewerTheme,
        );

        const detailsHtml = (effectiveDetails || [])
          .map(
            (d) =>
              `<div class="item"><div class="label">${String(d.label)}</div><div class="value">${
                typeof d.value === 'string' || typeof d.value === 'number'
                  ? d.value
                  : ''
              }</div></div>`,
          )
          .join('');

        const html = `<!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <title>${effectivePageTitle} - ${effectivePdfTitle}</title>
            <style>${styles}</style>
          </head>
          <body>
            <div class="container">
              <div class="header">
                <div class="title">${effectivePageTitle}</div>
              </div>
              <div class="details">${detailsHtml}</div>
              <div class="frameWrap">
                <iframe src="${effectiveUrl}" allowfullscreen title="${effectivePdfTitle}"></iframe>
              </div>
            </div>
          </body>
        </html>`;

        const blob = new Blob([html], { type: 'text/html' });
        const blobUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = blobUrl;
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      } catch (error) {
        console.error(error);
        toaster.create({
          type: 'error',
          title: 'Failed to open',
          description: 'There was a problem preparing the document.',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [url, pdfTitle, pageTitle, details, theme, onClick, onBeforeOpen],
  );

  return (
    <Tooltip content={!isDisabled ? '' : 'No file to open'}>
      <Box>
        <Button
          aria-label="open-pdf-in-new-tab"
          onClick={handleOpen}
          size="xs"
          fontSize="xs"
          variant="plain"
          whiteSpace="nowrap"
          color="secondary.solid"
          borderColor="secondary.emphasized"
          p={2}
          {...buttonProps}
          {...rest}
          disabled={isDisabled || isLoading}
        >
          {buttonIcon}
          {buttonText}
        </Button>
        {isInfoTip && <InfoTip>"Will open pdf in a new tab"</InfoTip>}
      </Box>
    </Tooltip>
  );
};

export type ViewPdfDetailItem = { label: string; value: React.ReactNode };

export interface ViewPdfProps {
  url: string;
  pdfTitle: string;
  pageTitle?: string;
  details?: ViewPdfDetailItem[];
  height?: string | number;
  isDisabled?: boolean;
  showOpenTabButton?: boolean;
  buttonProps?: OpenPdfTabButtonProps;
}

export const ViewPdf: React.FC<ViewPdfProps> = ({
  url,
  pdfTitle,
  pageTitle = 'Document Preview',
  details = [],
  height = '480px',
  isDisabled = false,
  showOpenTabButton = false,
  buttonProps,
}) => {
  return (
    <Box display="flex" flexDirection="column" gap={4} width="full">
      <HStack width="100%" justifyContent="space-between">
        <Text textStyle="heading.card" color="secondary.fg">
          {pageTitle}
        </Text>
        {showOpenTabButton && (
          <OpenPdfTabButton
            url={url}
            pdfTitle={pdfTitle}
            pageTitle={pageTitle}
            details={details}
            isDisabled={isDisabled}
            {...buttonProps}
          />
        )}
      </HStack>
      {details && details.length > 0 && (
        <Box>
          <RenderFormElements
            items={details.map((d) => {
              const valueType = typeof d.value;
              const isPrimitive =
                valueType === 'string' ||
                valueType === 'number' ||
                valueType === 'boolean' ||
                d.value == null;
              return isPrimitive
                ? {
                    label: String(d.label),
                    currentValue: d.value as
                      | string
                      | number
                      | boolean
                      | null
                      | undefined,
                  }
                : { label: String(d.label), children: <>{d.value}</> };
            })}
          />
        </Box>
      )}
      <Box
        borderWidth="1px"
        borderRadius="md"
        overflow="scroll"
        bg="bg.canvas"
        height={height}
      >
        {url ? (
          <iframe
            key={pdfTitle}
            allowFullScreen
            loading="eager"
            src={url}
            title={pdfTitle}
            width="100%"
            height="100%"
          />
        ) : (
          <EmptyStateBox title="No Results" description="No PDF to preview." />
        )}
      </Box>
    </Box>
  );
};

// Utility function for opening multiple PDFs externally

export type OpenMultiplePdfsType = {
  url: string;
  pdfTitle: string;
  pageTitle?: string;
  details?: DetailItem[];
};
export const openMultiplePdfs = (
  pdfItems: OpenMultiplePdfsType[],
  config?: PdfOpenConfig,
) => {
  const theme =
    config?.theme === 'light' ? lightPdfViewerTheme : darkPdfViewerTheme;
  const defaultPageTitle = config?.defaultPageTitle || 'Document Preview';
  const messages = config?.messages || {};

  if (!pdfItems || pdfItems.length === 0) {
    toaster.create({
      type: 'error',
      title: 'Error',
      description: messages.noPdfsError || 'No PDFs to open',
    });
    return;
  }

  let successCount = 0;
  let warningCount = 0;

  pdfItems.forEach(
    ({ url, pdfTitle, pageTitle = defaultPageTitle, details = [] }) => {
      if (!url) {
        warningCount++;
        return;
      }

      const styles = generateStylesFromTheme(theme);

      const detailsHtml = details
        .map(
          (d) =>
            `<div class="item"><div class="label">${String(d.label)}</div><div class="value">${
              typeof d.value === 'string' || typeof d.value === 'number'
                ? d.value
                : ''
            }</div></div>`,
        )
        .join('');

      const html = `<!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>${pageTitle} - ${pdfTitle}</title>
          <style>${styles}</style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <div class="title">${pageTitle}</div>
            </div>
            <div class="details">${detailsHtml}</div>
            <div class="frameWrap">
              <iframe src="${url}" allowfullscreen title="${pdfTitle}"></iframe>
            </div>
          </div>
        </body>
      </html>`;

      const blob = new Blob([html], { type: 'text/html' });
      const blobUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);

      successCount++;
    },
  );

  // Show summary message
  if (successCount > 0) {
    toaster.create({
      type: 'success',
      title: messages.successTitle || 'Success',
      description: messages.successDescription
        ? messages.successDescription(successCount, warningCount)
        : `Opened ${successCount} PDF(s) in separate tabs${warningCount > 0 ? ` (${warningCount} with no files)` : ''}`,
    });
  }
};

// Multi-PDF Button Component
export interface OpenMultiplePdfTabButtonProps extends Omit<
  ButtonProps,
  'onClick'
> {
  pdfItems: Array<{
    url: string;
    pdfTitle: string;
    pageTitle?: string;
    details?: DetailItem[];
  }>;

  buttonText?: string | React.ReactNode | null;
  buttonIcon?: React.ReactNode;
  isDisabled?: boolean;
  isInfoTip?: boolean;
  config?: PdfOpenConfig;
  [key: string]: any;
}

export const OpenMultiplePdfTabButton: React.FC<
  OpenMultiplePdfTabButtonProps
> = ({
  pdfItems,
  buttonText = 'Open Multiple PDFs',
  buttonIcon = <AiOutlineExport />,
  isDisabled = false,
  isInfoTip = true,
  config,
  ...buttonProps
}) => {
  const handleOpen = React.useCallback(() => {
    openMultiplePdfs(pdfItems, config);
  }, [pdfItems, config]);

  const hasValidPdfs = pdfItems && pdfItems.some((item) => item.url);

  return (
    <Tooltip content={!isDisabled && hasValidPdfs ? '' : 'No PDFs to open'}>
      <Button
        aria-label="open-multiple-pdfs-in-new-tabs"
        onClick={handleOpen}
        variant="outline"
        color="tertiary.fg"
        size="sm"
        {...buttonProps}
        disabled={isDisabled || !hasValidPdfs}
      >
        {buttonIcon}
        {buttonText}
      </Button>
      {isInfoTip && (
        <InfoTip>"Will open multiple PDFs in separate tabs"</InfoTip>
      )}
    </Tooltip>
  );
};

export default ViewPdf;
